/**
 * Date utility functions with safe validation to prevent RangeError
 * when formatting invalid dates.
 */

import { formatDistanceToNow, format, isValid } from "date-fns";

/**
 * Check if a date value is valid and can be formatted.
 */
export function isValidDate(date: Date | null | undefined): date is Date {
  return date instanceof Date && isValid(date) && !isNaN(date.getTime());
}

/**
 * Safely parse a date string or timestamp into a Date object.
 * Returns null if the value cannot be parsed into a valid date.
 */
export function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined) {
    return null;
  }

  try {
    const date = value instanceof Date ? value : new Date(value);
    return isValidDate(date) ? date : null;
  } catch {
    return null;
  }
}

/**
 * Safely format a date using formatDistanceToNow.
 * Returns the fallback string if the date is invalid.
 *
 * @param value - Date string, timestamp, or Date object
 * @param options - Options for formatDistanceToNow (e.g., { addSuffix: true })
 * @param fallback - Fallback string for invalid dates (default: "Unknown")
 */
export function safeFormatDistanceToNow(
  value: string | number | Date | null | undefined,
  options?: { addSuffix?: boolean; includeSeconds?: boolean },
  fallback: string = "Unknown"
): string {
  const date = parseDate(value);
  if (!date) {
    return fallback;
  }

  try {
    return formatDistanceToNow(date, options);
  } catch {
    return fallback;
  }
}

/**
 * Safely format a date using a format string.
 * Returns the fallback string if the date is invalid.
 *
 * @param value - Date string, timestamp, or Date object
 * @param formatString - Format string for date-fns format function
 * @param fallback - Fallback string for invalid dates (default: "Unknown")
 */
export function safeFormatDate(
  value: string | number | Date | null | undefined,
  formatString: string,
  fallback: string = "Unknown"
): string {
  const date = parseDate(value);
  if (!date) {
    return fallback;
  }

  try {
    return format(date, formatString);
  } catch {
    return fallback;
  }
}
